// Where a visitor came from has to be captured on the FIRST page they land on.
// By the time the sign-up form is reached the query string is long gone: the ad
// lands on the marketing site, the visitor is handed over to the offers app, and
// React Router then replaces the URL on every funnel step. Reading the params at
// the form (as we used to) meant paid traffic was indistinguishable from direct.

const STORAGE_KEY = "kahunaAttribution"

// Meta rewrites every ad click with fbclid, so that parameter alone is proof of a
// paid click even when the campaign forgot to tag the link. The utm_source values
// cover the links we tag ourselves.
const META_UTM_SOURCES = ["facebook", "fb", "instagram", "ig", "meta"]

export type Attribution = {
  source: string
  medium: string
  campaign: string
  fbclid: string
  landedAt: number
}

const emptyAttribution: Attribution = {
  source: "",
  medium: "",
  campaign: "",
  fbclid: "",
  landedAt: 0
}

// scouting-app-api stores this in User.lead_follow, a CharField(max_length=63), and
// prints it verbatim as "Source:" in #bot-lead-follow. Keep it to a shape both a
// database column and a Slack line can render: lowercase, no spaces, no punctuation.
const LEAD_FOLLOW_MAX_LENGTH = 63

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40)

// A long utm_source plus a long utm_campaign can outgrow the column on its own, so
// the assembled value is clamped rather than trusted to fit.
const clamp = (leadFollow: string): string => leadFollow.slice(0, LEAD_FOLLOW_MAX_LENGTH).replace(/_+$/, "")

const read = (): Attribution => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...emptyAttribution }
    return { ...emptyAttribution, ...JSON.parse(raw) }
  } catch {
    // Private mode, corrupted JSON - attribution is never worth breaking a sign-up for.
    return { ...emptyAttribution }
  }
}

const write = (attribution: Attribution): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
  } catch {
    // See read(): storage is best effort.
  }
}

// Call as early as possible on every page load. Last touch wins, which is what ad
// platforms report against: a visitor who clicks a second ad is credited to it.
export const captureAttribution = (): void => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)
  const fbclid = params.get("fbclid") || ""
  const utmSource = params.get("utm_source") || ""
  const utmMedium = params.get("utm_medium") || ""
  const utmCampaign = params.get("utm_campaign") || ""

  // Nothing to learn from this load; keep whatever the landing page already stored.
  if (!fbclid && !utmSource && !utmMedium && !utmCampaign) return

  write({
    source: utmSource,
    medium: utmMedium,
    campaign: utmCampaign,
    fbclid,
    landedAt: Date.now()
  })
}

export const getAttribution = (): Attribution => read()

export const isMetaTraffic = (attribution: Attribution = read()): boolean =>
  Boolean(attribution.fbclid) || META_UTM_SOURCES.includes(attribution.source.toLowerCase())

// Meta's click identifier cookie format: fb.<subdomainIndex>.<creationTime>.<fbclid>.
// Nothing consumes it yet - it is what the Conversions API will need to match a
// server side CompleteRegistration back to the ad click.
export const getFbc = (): string => {
  const attribution = read()
  if (!attribution.fbclid) return ""
  return `fb.1.${attribution.landedAt || Date.now()}.${attribution.fbclid}`
}

// The single string the backend stores and the bot-lead-follow Slack bot prints as
// "Source:". Paid traffic is named first on purpose: personalizedLinkCode falls back
// to "funding" for everyone, so as long as it was checked first every Meta click was
// reported as "personalized_link_funding" and the campaign branch was dead code.
export const resolveLeadFollow = (personalizedLinkCode: string): string => {
  const attribution = read()

  if (isMetaTraffic(attribution)) {
    return clamp(attribution.campaign ? `lead_follow_meta_${slug(attribution.campaign)}` : "lead_follow_meta")
  }

  if (attribution.source) {
    return clamp(
      attribution.campaign
        ? `lead_follow_${slug(attribution.source)}_${slug(attribution.campaign)}`
        : `lead_follow_${slug(attribution.source)}`
    )
  }

  if (attribution.campaign) {
    return clamp(`lead_follow_${slug(attribution.campaign)}`)
  }

  if (personalizedLinkCode) {
    return clamp(`personalized_link_${personalizedLinkCode}`)
  }

  return "lead_follow_direct"
}
